/* eslint-disable no-magic-numbers,max-statements,max-lines */
import uuid from "uuid/v4";
import keyBy from "lodash/keyBy";
import without from "lodash/without";
import uniq from "lodash/uniq";
import omit from "lodash/omit";
import fromPairs from "lodash/fromPairs";
import difference from "lodash/difference";
import moment from "moment-timezone";
import TopicStatus from "@/constants/TopicStatus";
import MeetingTabs from "@/constants/Schedule/MeetingTabs";
import MeetingPermissions from "@/permissions/MeetingPermissions";
import TopicPermissions from "@/permissions/TopicPermissions";
import { createNewTopic } from "@/store/modules/topicHelpers";
import { create as createOutcomeHelper } from "@/store/modules/outcomeHelpers";

// events
import {
	MeetingTopicPinnedEvent,
	MeetingTopicRequestSuggestionMadeEvent,
	MeetingTopicUnpinnedEvent
} from "@/telemetry";

import { MEETING_SERIES_PAGE_SIZE } from "./constants";

const createMeetingMaps = ( meetings, userProfile, timezone ) => {
	const dateMeetingMap = {};
	const meetingTopicRequestMap = {};
	const meetingTopicMap = {};
	const dirtyTopicFlagMap = {
		"": false, // new topics are not dirty
	};
	const meetingOutcomeIdMap = {};
	const meetingSeriesMap = {};
	const meetingSeriesOffsetMap = {};
	const meetingPermissionsMap = {};
	const topicPermissionsMap = {};
	const topicDeferralMap = {
		"": false,
	};
	const meetingInviteeMap = {};
	const outcomeMap = {};

	Object.keys( meetings ).forEach( meetingId => {
		//
		// meeting
		//
		const meeting = meetings[ meetingId ];

		const meetingPermissions = new MeetingPermissions( { meeting, userProfile, timezone, } );
		meetingPermissionsMap[ meetingId ] = meetingPermissions.resolve();

		const meetingDate = moment.tz( meeting.plannedStart * 1000, timezone )
			.startOf( "day" );
		const formattedMeetingDate = meetingDate.format( "YYYY-MM-DD" );
		dateMeetingMap[ formattedMeetingDate ] = dateMeetingMap[ formattedMeetingDate ] || [];
		dateMeetingMap[ formattedMeetingDate ].push( meetingId );

		meetingTopicRequestMap[ meetingId ] = meeting.requestSent;

		if ( meeting.meetingSeriesId ) {
			meetingSeriesMap[ meeting.meetingSeriesId ] = {};
			meetingSeriesOffsetMap[ meeting.meetingSeriesId ] = 0;
		}

		//
		// topics
		//
		const sortedTopics = meeting.topics
			.map( topic => {
				dirtyTopicFlagMap[ topic.id ] = false;
				return {
					// use a surrogate UI key for loops so we can always
					// force a re-render when topic content changes
					uiKey: uuid(),
					...topic,
				};
			} );

		meetingTopicMap[ meetingId ] = sortedTopics;

		meetingOutcomeIdMap[ meetingId ] = [];

		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		sortedTopics.forEach( topic => {
			//
			// outcomes
			//
			topic.outcomes.forEach( outcome => {
				meetingOutcomeIdMap[ meetingId ].push( outcome.id );

				outcomeMap[ outcome.id ] = {
					...outcome,
					permissions: outcomeHelper.createPermissions( outcome ),
				};
			} );

			const topicPermissions = new TopicPermissions( { topic, userProfile, timezone, meetingPermissions, } );
			topicPermissionsMap[ topic.id ] = topicPermissions.resolve();

			topicDeferralMap[ topic.id ] = topic.status === TopicStatus.DEFERRED;
		} );

		const { invitees = [], } = meeting;
		meetingInviteeMap[ meetingId ] = keyBy( invitees, "personId" );
	} );

	return {
		dateMeetingMap,
		meetingTopicRequestMap,
		meetingTopicMap,
		meetingOutcomeIdMap,
		meetingSeriesMap,
		meetingSeriesOffsetMap,
		meetingPermissionsMap,
		topicPermissionsMap,
		topicDeferralMap,
		meetingInviteeMap,
		outcomeMap,
	};
};

const reactions = {
	scheduleRefreshed: ( state, { meetings, offset, userProfile, timezone, } ) => {
		meetings = keyBy( meetings, "id" );
		const currentMeetings = state.meetings;
		const currentMeetingTopicMap = state.meetingTopicMap;
		const currentMeetingTopicRequestMap = state.meetingTopicRequestMap;
		const currentMeetingOutcomeIdMap = state.meetingOutcomeIdMap;
		const currentMeetingInviteeMap = state.meetingInviteeMap;
		const currentMeetingPermissionsMap = state.meetingPermissionsMap;
		const currentDateMeetingMap = state.dateMeetingMap;

		/*
			If a meeting has been removed from the user's calendar, that
			meetingId will not be in the newMeetingIds array, but
			it will still be in the currentMeetingIds array. We capture
			these ids in meetingIdsToRemove so that we can remove them
			from store state
		*/
		const newMeetingIds = Object.keys( meetings );
		const currentMeetingIds = state.offsetMeetingMap[ offset ];
		const meetingIdsToRemove = without( currentMeetingIds, ...newMeetingIds );

		meetingIdsToRemove.forEach( meetingId => {
			delete currentMeetings[ meetingId ];
			delete currentMeetingTopicMap[ meetingId ];
			delete currentMeetingTopicRequestMap[ meetingId ];
			delete currentMeetingOutcomeIdMap[ meetingId ];
			delete currentMeetingInviteeMap[ meetingId ];
			delete currentMeetingPermissionsMap[ meetingId ];
		} );

		Object.keys( currentDateMeetingMap ).forEach( dateKey => {
			currentDateMeetingMap[ dateKey ] = without( currentDateMeetingMap[ dateKey ], ...meetingIdsToRemove );
		} );

		state.meetings = {
			...currentMeetings,
			...meetings,
		};

		state.offsetMeetingMap = {
			...state.offsetMeetingMap,
			[ offset ]: newMeetingIds,
		};

		const {
			dateMeetingMap,
			meetingTopicMap,
			meetingTopicRequestMap,
			dirtyTopicFlagMap,
			meetingSeriesMap,
			meetingSeriesOffsetMap,
			meetingPermissionsMap,
			topicPermissionsMap,
			topicDeferralMap,
			meetingInviteeMap,
			meetingOutcomeIdMap,
			outcomeMap,
		} = createMeetingMaps( state.meetings, userProfile, timezone );

		state.dateMeetingMap = {
			...currentDateMeetingMap,
			...dateMeetingMap,
		};

		state.meetingTopicMap = {
			...currentMeetingTopicMap,
			...meetingTopicMap,
		};

		state.meetingTopicRequestMap = {
			...currentMeetingTopicRequestMap,
			...meetingTopicRequestMap,
		};

		state.dirtyTopicFlagMap = {
			...state.dirtyTopicFlagMap,
			...dirtyTopicFlagMap,
		};

		state.invalidatedOffsets = without( state.invalidatedOffsets, offset );

		state.meetingOutcomeIdMap = {
			...currentMeetingOutcomeIdMap,
			...meetingOutcomeIdMap,
		};

		state.outcomeMap = {
			...state.outcomeMap,
			...outcomeMap,
		};

		// If all meetings in a *series* have been removed, we need to prune
		// those series from the meetingSeriesMap as well.
		const currentMeetingSeriesMap = {};
		const currentMeetingSeriesOffsetMap = {};
		uniq(
			Object.keys( state.meetings )
			.map( meetingId => state.meetings[ meetingId ].meetingSeriesId )
			.filter( meetingSeriesId => !!meetingSeriesId )
		).forEach( meetingSeriesId => {
			// we also need to remove any individual meetings from series that may not
			// have been entirely omitted
			currentMeetingSeriesMap[ meetingSeriesId ] = omit(
				state.meetingSeriesMap[ meetingSeriesId ],
				meetingIdsToRemove
			);
			currentMeetingSeriesOffsetMap[ meetingSeriesId ] =
				state.meetingSeriesOffsetMap[ meetingSeriesId ] || 0;
		} );

		state.meetingSeriesMap = {
			...meetingSeriesMap, // intentionally first
			...currentMeetingSeriesMap,
		};

		state.meetingSeriesOffsetMap = {
			...meetingSeriesOffsetMap,
			...currentMeetingSeriesOffsetMap,
		};

		state.meetingPermissionsMap = {
			...currentMeetingPermissionsMap,
			...meetingPermissionsMap,
		};

		state.topicPermissionsMap = {
			...state.topicPermissionsMap,
			...topicPermissionsMap,
		};

		state.topicDeferralMap = {
			...state.topicDeferralMap,
			...topicDeferralMap,
		};

		state.meetingInviteeMap = {
			...currentMeetingInviteeMap,
			...meetingInviteeMap,
		};
	},
	userProfileUpdated: ( state, { userProfile, timezone, } ) => {
		const {
			meetingPermissionsMap,
			topicPermissionsMap,
		} = createMeetingMaps( state.meetings, userProfile, timezone );

		state.meetingPermissionsMap = {
			...state.meetingPermissionsMap,
			...meetingPermissionsMap,
		};

		state.topicPermissionsMap = {
			...state.topicPermissionsMap,
			...topicPermissionsMap,
		};
	},
	weekOffsetUpdated: ( state, { offset, } ) => {
		state.selectedWeekOffset = offset;
	},
	/** TODO: topics are a meeting agenda store concern - move */
	topicsRequested: ( state, { meetingId, userProfile, timezone, } ) => {
		state.meetingTopicRequestMap = {
			...state.meetingTopicRequestMap,
			[ meetingId ]: true,
		};

		const meeting = state.meetings[ meetingId ];
		const updatedMeeting = {
			...meeting,
			requestSent: true,
		};

		state.meetings = {
			...state.meetings,
			[ meetingId ]: updatedMeeting,
		};

		const meetingPermissions = new MeetingPermissions( { meeting: updatedMeeting, userProfile, timezone, } );
		state.meetingPermissionsMap = {
			...state.meetingPermissionsMap,
			[ meetingId ]: meetingPermissions.resolve(),
		};

		const event = new MeetingTopicRequestSuggestionMadeEvent( meeting.code );
		event.submit();
	},
	meetingUpdated: ( state, { meeting, userProfile, timezone, } ) => {
		state.meetings = {
			...state.meetings,
			[ meeting.id ]: meeting,
		};

		const {
			dateMeetingMap,
			meetingTopicMap,
			meetingTopicRequestMap,
			dirtyTopicFlagMap,
			meetingSeriesMap,
			meetingPermissionsMap,
			topicPermissionsMap,
			topicDeferralMap,
			meetingInviteeMap,
			meetingOutcomeIdMap,
			outcomeMap,
		} = createMeetingMaps( state.meetings, userProfile, timezone );

		state.dateMeetingMap = {
			...state.dateMeetingMap,
			...dateMeetingMap,
		};

		// preserve new topics if already added
		const newTopic = state.meetingTopicMap[ meeting.id ]?.find( topic => topic.id === "" );
		if ( newTopic ) {
			meetingTopicMap[ meeting.id ].push( { ...newTopic, } );
		}

		state.meetingTopicMap = {
			...state.meetingTopicMap,
			...meetingTopicMap,
		};

		state.meetingTopicRequestMap = {
			...state.meetingTopicRequestMap,
			...meetingTopicRequestMap,
		};

		state.dirtyTopicFlagMap = {
			...state.dirtyTopicFlagMap,
			...dirtyTopicFlagMap,
			"": ( newTopic && !!( newTopic.title || newTopic.description ) ),
		};

		state.meetingOutcomeIdMap = {
			...state.meetingOutcomeIdMap,
			...meetingOutcomeIdMap,
		};

		state.outcomeMap = {
			...state.outcomeMap,
			...outcomeMap,
		};

		state.meetingSeriesMap = {
			...meetingSeriesMap, // intentionally first
			...state.meetingSeriesMap,
		};

		// update the meeting within the series map, if necessary
		const { meetingId, meetingSeriesId = null, } = meeting;
		if ( meetingSeriesId ) {
			const meetingSeries = state.meetingSeriesMap[ meetingSeriesId ];
			state.meetingSeriesMap[ meetingSeriesId ] = {
				...meetingSeries,
				[ meetingId ]: {
					...meeting,
				},
			};
		}

		state.meetingPermissionsMap = {
			...state.meetingPermissionsMap,
			...meetingPermissionsMap,
		};

		state.topicPermissionsMap = {
			...state.topicPermissionsMap,
			...topicPermissionsMap,
		};

		state.topicDeferralMap = {
			...state.topicDeferralMap,
			...topicDeferralMap,
		};

		state.meetingInviteeMap = {
			...state.meetingInviteeMap,
			...meetingInviteeMap,
		};
	},
	/** TODO: topics are a meeting agenda store concern - move */
	meetingTopicCreated: ( state, { meetingId, createdTopic, userProfile, timezone, } ) => {
		const meeting = state.meetings[ meetingId ];
		const meetingPermissions = new MeetingPermissions( { meeting, userProfile, timezone, } );

		const mergedTopics = state.meetingTopicMap[ meetingId ]
			.filter( existingTopic => !!existingTopic.id )
			.concat( [ createdTopic, ] )
			.map( ( orderedTopic /* , index*/ ) => {
				return {
					uiKey: uuid(),
					...orderedTopic,
				};
			} );

		state.meetingTopicMap = {
			...state.meetingTopicMap,
			[ meetingId ]: [ ...mergedTopics, ],
		};

		state.dirtyTopicFlagMap = {
			...state.dirtyTopicFlagMap,
			...fromPairs( mergedTopics.map( mergedTopic => [ mergedTopic.id, false, ] ) ),
			"": false,
		};

		const meetingOutcomeIdMap = {
			[ meetingId ]: [],
		};

		const outcomeMap = {};

		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		mergedTopics.forEach( topic => {
			topic.outcomes.forEach( outcome => {
				meetingOutcomeIdMap[ meetingId ].push( outcome.id );
				outcomeMap[ outcome.id ] = {
					...outcome,
					permissions: outcomeHelper.createPermissions( outcome ),
				};
			} );
		} );

		state.meetingOutcomeIdMap = {
			...state.meetingOutcomeIdMap,
			...meetingOutcomeIdMap,
		};

		state.outcomeMap = {
			...state.outcomeMap,
			...outcomeMap,
		};

		const topicPermission = new TopicPermissions( { topic: createdTopic, userProfile, timezone, meetingPermissions, } );
		state.topicPermissionsMap = {
			...state.topicPermissionsMap,
			[ createdTopic.id ]: topicPermission.resolve(),
		};

		state.topicDeferralMap = {
			...state.topicDeferralMap,
			[ createdTopic.id ]: createdTopic.status === TopicStatus.DEFERRED,
		};
	},
	/** TODO: topics are a meeting agenda store concern - move */
	meetingTopicsUpdated: ( state, { meetingId, updatedTopics, userProfile, timezone, } ) => {
		const meeting = state.meetings[ meetingId ];
		const meetingPermissions = new MeetingPermissions( { meeting, userProfile, timezone, } );

		// If there's an existing "new topic", we want to preserve it
		// so the user can continue to edit it after existing topics are updated
		const existingNewTopic = state.meetingTopicMap[ meetingId ]
			.find( existingTopic => existingTopic.id === "" );

		if ( existingNewTopic ) {
			updatedTopics = updatedTopics.concat( [ {
				...existingNewTopic,
			}, ] );
		}

		state.meetingTopicMap = {
			...state.meetingTopicMap,
			[ meetingId ]: updatedTopics.map( topic => {
				return {
					uiKey: uuid(),
					...topic,
				};
			} ),
		};

		state.dirtyTopicFlagMap = {
			...state.dirtyTopicFlagMap,
			...fromPairs( updatedTopics.map( updatedTopic => [ updatedTopic.id, false, ] ) ),
			"": ( existingNewTopic && !!( existingNewTopic.title || existingNewTopic.description ) ),
		};

		const meetingOutcomeIdMap = {
			[ meetingId ]: [],
		};

		const outcomeMap = {};

		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		updatedTopics.forEach( topic => {
			topic.outcomes.forEach( outcome => {
				meetingOutcomeIdMap[ meetingId ].push( outcome.id );
				outcomeMap[ outcome.id ] = {
					...outcome,
					permissions: outcomeHelper.createPermissions( outcome ),
				};
			} );
		} );

		state.meetingOutcomeIdMap = {
			...state.meetingOutcomeIdMap,
			...meetingOutcomeIdMap,
		};

		state.outcomeMap = {
			...state.outcomeMap,
			...outcomeMap,
		};

		const topicPermissionsMap = { ...state.topicPermissionsMap, };
		updatedTopics.forEach( updatedTopic => {
			const topicPermissions = new TopicPermissions( { topic: updatedTopic, userProfile, timezone, meetingPermissions, } );
			topicPermissionsMap[ updatedTopic.id ] = topicPermissions.resolve();
		} );
		state.topicPermissionsMap = topicPermissionsMap;

		state.topicDeferralMap = {
			...state.topicDeferralMap,
			...fromPairs( updatedTopics, topic => [
				topic.id,
				topic.status === TopicStatus.DEFERRED,
			] ),
		};
	},
	/** TODO: topics are a meeting agenda store concern - move */
	topicDeferred: ( state, { meetingId, topicId, userProfile, timezone, } ) => {
		const topics = state.meetingTopicMap[ meetingId ];
		const deferredTopic = {
			...topics.find( topic => topic.id === topicId ),
			status: TopicStatus.DEFERRED,
		};

		const topicIndex = topics.findIndex( t => t.id === topicId );

		const updatedTopics = topics.filter( t => t.id !== topicId );
		updatedTopics.splice( topicIndex, 0, deferredTopic );

		state.meetingTopicMap = {
			...state.meetingTopicMap,
			[ meetingId ]: updatedTopics.map( topic => {
				return {
					uiKey: uuid(),
					...topic,
				};
			} ),
		};

		const meeting = state.meetings[ meetingId ];
		const meetingPermissions = new MeetingPermissions( { meeting, userProfile, timezone, } );
		const topicPermissions = new TopicPermissions( { topic: deferredTopic, userProfile, timezone, meetingPermissions, } );

		state.topicPermissionsMap = {
			...state.topicPermissionsMap,
			[ deferredTopic.id ]: topicPermissions.resolve(),
		};

		state.topicDeferralMap = {
			...state.topicDeferralMap,
			[ topicId ]: true,
		};
	},
	/** TODO: topics are a meeting agenda store concern - move */
	topicUndeferred: ( state, { meetingId, topicId, userProfile, timezone, } ) => {
		const topics = state.meetingTopicMap[ meetingId ];
		const undeferredTopic = {
			...topics.find( topic => topic.id === topicId ),
			status: TopicStatus.PLANNED,
		};

		const topicIndex = topics.findIndex( t => t.id === topicId );

		const updatedTopics = topics.filter( t => t.id !== topicId );
		updatedTopics.splice( topicIndex, 0, undeferredTopic );

		state.meetingTopicMap = {
			...state.meetingTopicMap,
			[ meetingId ]: updatedTopics.map( topic => {
				return {
					uiKey: uuid(),
					...topic,
				};
			} ),
		};

		const meeting = state.meetings[ meetingId ];
		const meetingPermissions = new MeetingPermissions( { meeting, userProfile, timezone, } );
		const topicPermissions = new TopicPermissions( { topic: undeferredTopic, userProfile, timezone, meetingPermissions, } );

		state.topicPermissionsMap = {
			...state.topicPermissionsMap,
			[ undeferredTopic.id ]: topicPermissions.resolve(),
		};

		state.topicDeferralMap = {
			...state.topicDeferralMap,
			[ topicId ]: false,
		};
	},
	/** TODO: topics are a meeting agenda store concern - move */
	topicPinned: ( state, { meetingId, topic, userProfile, timezone, } ) => {
		const topics = state.meetingTopicMap[ meetingId ];
		const updatedTopic = {
			...topics.find( t => t.id === topic.id ),
			...topic,
		};

		const topicIndex = topics.findIndex( t => t.id === topic.id );

		const updatedTopics = topics.filter( t => t.id !== topic.id );
		updatedTopics.splice( topicIndex, 0, updatedTopic );

		state.meetingTopicMap = {
			...state.meetingTopicMap,
			[ meetingId ]: updatedTopics.map( t => {
				return {
					uiKey: uuid(),
					...t,
				};
			} ),
		};

		const meeting = state.meetings[ meetingId ];
		const meetingPermissions = new MeetingPermissions( { meeting, userProfile, timezone, } );
		const topicPermissions = new TopicPermissions( { topic, userProfile, timezone, meetingPermissions, } );

		state.topicPermissionsMap = {
			...state.topicPermissionsMap,
			[ topic.id ]: topicPermissions.resolve(),
		};

		const telemetryEvent = new MeetingTopicPinnedEvent( meetingId );
		telemetryEvent.submit();
	},
	// NOTE - currently identical to topicPinned. May be able to combine them if this stays the case
	/** TODO: topics are a meeting agenda store concern - move */
	topicUnpinned: ( state, { meetingId, topic, userProfile, timezone, } ) => {
		const topics = state.meetingTopicMap[ meetingId ];
		const updatedTopic = {
			...topics.find( t => t.id === topic.id ),
			...topic,
		};

		const topicIndex = topics.findIndex( t => t.id === topic.id );

		const updatedTopics = topics.filter( t => t.id !== topic.id );
		updatedTopics.splice( topicIndex, 0, updatedTopic );

		state.meetingTopicMap = {
			...state.meetingTopicMap,
			[ meetingId ]: updatedTopics.map( t => {
				return {
					uiKey: uuid(),
					...t,
				};
			} ),
		};

		const meeting = state.meetings[ meetingId ];
		const meetingPermissions = new MeetingPermissions( { meeting, userProfile, timezone, } );
		const topicPermissions = new TopicPermissions( { topic, userProfile, timezone, meetingPermissions, } );

		state.topicPermissionsMap = {
			...state.topicPermissionsMap,
			[ topic.id ]: topicPermissions.resolve(),
		};

		const telemetryEvent = new MeetingTopicUnpinnedEvent( meetingId );
		telemetryEvent.submit();
	},
	/** TODO: topics are a meeting agenda store concern - move */
	topicInstanceUnpinned: ( state, { meetingId, topicId, } ) => {
		state.meetingTopicRequestMap = {
			...omit( state.meetingTopicRequestMap, topicId ),
		};

		const meetingTopics = state.meetingTopicMap[ meetingId ]
			.filter( topic => topic.id !== topicId );

		state.meetingTopicMap[ meetingId ] = meetingTopics;

		state.dirtyTopicFlagMap = {
			...omit( state.dirtyTopicFlagMap, topicId ),
		};

		state.topicPermissionsMap = {
			...omit( state.topicPermissionsMap, topicId ),
		};

		state.topicDeferralMap = {
			...omit( state.topicDeferralMap, topicId ),
		};
	},
	/** TODO: outcomes are a meeting agenda store concern - move */
	outcomesFetched: ( state, { meetingId, outcomes, userProfile, timezone, } ) => {
		state.meetingOutcomeIdMap = {
			...state.meetingOutcomeIdMap,
			[ meetingId ]: outcomes.map( outcome => outcome.id ),
		};

		const meeting = state.meetings[ meetingId ];

		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		state.outcomeMap = {
			...state.outcomeMap,
			...fromPairs( outcomes.map( outcome => {
				return [ outcome.id, {
					...outcome,
					permissions: outcomeHelper.createPermissions( outcome ),
				}, ];
			} ) ),
		};
	},
	/** TODO: outcomes are a meeting agenda store concern - move */
	outcomeCreated: ( state, { meetingId, createdOutcome, userProfile, timezone, } ) => {
		const meetingOutcomeIds = state.meetingOutcomeIdMap[ meetingId ]
			.concat( [ createdOutcome.id, ] );

		state.meetingOutcomeIdMap = {
			...state.meetingOutcomeIdMap,
			[ meetingId ]: meetingOutcomeIds,
		};

		const meeting = state.meetings[ meetingId ];

		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		state.outcomeMap = {
			...state.outcomeMap,
			[ createdOutcome.id ]: {
				...createdOutcome,
				permissions: outcomeHelper.createPermissions( createdOutcome ),
			},
		};
	},
	/** TODO: outcomes are a meeting agenda store concern - move */
	outcomeUpdated: ( state, { meetingId, updatedOutcome, userProfile, timezone, } ) => {
		const meetingOutcomeIds = state.meetingOutcomeIdMap[ meetingId ]
			.concat( [ updatedOutcome.id, ] );

		state.meetingOutcomeIdMap = {
			...state.meetingOutcomeIdMap,
			[ meetingId ]: uniq( meetingOutcomeIds ),
		};

		const meeting = state.meetings[ meetingId ];

		const outcomeHelper = createOutcomeHelper( { meeting, userProfile, timezone, } );

		state.outcomeMap = {
			...state.outcomeMap,
			[ updatedOutcome.id ]: {
				...updatedOutcome,
				permissions: outcomeHelper.createPermissions( updatedOutcome ),
			},
		};
	},
	meetingSeriesFetched: ( state, { meetingSeriesId, seriesMeetings, userProfile, timezone, } ) => {
		const currentMeetingSeries = state.meetingSeriesMap[ meetingSeriesId ];
		const offset = state.meetingSeriesOffsetMap[ meetingSeriesId ];
		const meetingSeriesMap = keyBy( seriesMeetings, "id" );

		state.meetingSeriesMap = {
			...state.meetingSeriesMap,
			[ meetingSeriesId ]: {
				...currentMeetingSeries,
				...meetingSeriesMap,
			},
		};

		state.meetingSeriesOffsetMap = {
			...state.meetingSeriesOffsetMap,
			[ meetingSeriesId ]: offset + MEETING_SERIES_PAGE_SIZE,
		};

		//
		// need to map meeting series outcomes because commitments
		// can still be marked as complete in the past meetings tab
		//
		const {
			meetingPermissionsMap,
			topicPermissionsMap,
			topicDeferralMap,
			meetingInviteeMap,
			meetingOutcomeIdMap,
			outcomeMap,
		} = createMeetingMaps( meetingSeriesMap, userProfile, timezone );

		state.meetingOutcomeIdMap = {
			...state.meetingOutcomeIdMap,
			...meetingOutcomeIdMap,
		};

		state.outcomeMap = {
			...state.outcomeMap,
			...outcomeMap,
		};

		state.meetingPermissionsMap = {
			...state.meetingPermissionsMap,
			...meetingPermissionsMap,
		};

		state.topicPermissionsMap = {
			...state.topicPermissionsMap,
			...topicPermissionsMap,
		};

		state.topicDeferralMap = {
			...state.topicDeferralMap,
			...topicDeferralMap,
		};

		state.meetingInviteeMap = {
			...state.meetingInviteeMap,
			...meetingInviteeMap,
		};
	},
	/** TODO: topics are a meeting agenda store concern - move */
	topicDeleted: ( state, { meetingId, topicId, } ) => {
		state.meetingTopicRequestMap = {
			...omit( state.meetingTopicRequestMap, topicId ),
		};

		const meetingTopics = state.meetingTopicMap[ meetingId ]
			.filter( topic => topic.id !== topicId );

		state.meetingTopicMap[ meetingId ] = meetingTopics;

		state.dirtyTopicFlagMap = {
			...omit( state.dirtyTopicFlagMap, topicId ),
		};

		state.topicPermissionsMap = {
			...omit( state.topicPermissionsMap, topicId ),
		};

		state.topicDeferralMap = {
			...omit( state.topicDeferralMap, topicId ),
		};
	},
};

const mutations = {
	selectScheduleDate: ( state, momentDate ) => {
		state.selectedScheduleDate = momentDate.clone();
	},
	toggleRefreshingSchedule: ( state, isRefreshing ) => {
		state.isRefreshingSchedule = isRefreshing;
	},
	selectedMeetingChanged: ( state, meetingId ) => {
		state.selectedScheduleMeetingId = meetingId;
		state.selectedMeetingTab = meetingId ? MeetingTabs.DEFAULT : "";
		state.newTopic = null;
	},
	/** TODO: meeting tab is a meeting store concern - move */
	selectMeetingTab: ( state, tab ) => {
		state.selectedMeetingTab = tab;
	},
	setFilterOption: ( state, filterOption ) => {
		state.selectedFilterOption = filterOption;
	},
	/** TODO: topics are a meeting agenda store concern - move */
	toggleIsRequestingTopics: ( state, isRequesting ) => {
		state.isRequestingTopics = isRequesting;
	},
	invalidateOffsetMeetingMap: state => {
		state.invalidatedOffsets = uniq( [
			...state.invalidatedOffsets, // existing invalidations
			...Object.keys( state.offsetMeetingMap )
			.map( offset => parseInt( offset, 10 ) ), // whatever offsets have already been loaded
		] );
	},
	toggleIsBackgroundRefreshWaiting: ( state, isRefreshWaiting ) => {
		state.isBackgroundRefreshWaiting = isRefreshWaiting;
	},
	toggleIsBackgroundUpdate: ( state, isBackgroundUpdate ) => {
		state.isBackgroundUpdate = isBackgroundUpdate;
	},
	/** TODO: topics are a meeting agenda store concern - move */
	toggleIsResortingTopics: ( state, isResorting ) => {
		state.isResortingTopics = isResorting;
	},
	/** TODO: topics are a meeting agenda store concern - move */
	toggleIsSavingTopics: ( state, isSaving ) => {
		state.isSavingTopics = isSaving;
	},
	/** TODO: topics are a meeting agenda store concern - move */
	addEmptyTopic: ( state, { meetingId, userProfile, timezone, } ) => {
		const meeting = state.meetings[ meetingId ];
		const meetingPermissions = new MeetingPermissions( { meeting, userProfile, timezone, } );

		const newTopic = createNewTopic( meetingId, userProfile );
		const newTopicPermissions = new TopicPermissions( { topic: newTopic, userProfile, timezone, meetingPermissions, } );

		state.newTopic = {
			topic: newTopic,
			permissions: newTopicPermissions,
		};
	},
	/** TODO: topics are a meeting agenda store concern - move */
	clearNewTopic: state => {
		state.newTopic = null;
	},
	/** TODO: topics are a meeting agenda store concern - move */
	setTopicDirtyFlag: ( state, { topicId, isDirty, } ) => {
		state.dirtyTopicFlagMap = {
			...state.dirtyTopicFlagMap,
			[ topicId ]: isDirty,
		};
	},
	/** TODO: topics are a meeting agenda store concern - move */
	toggleIsRemovingTopics: ( state, isRemoving ) => {
		state.isRemovingTopics = isRemoving;
	},
	toggleIsScheduleSyncing: ( state, isSyncing ) => {
		state.isScheduleSyncing = isSyncing;
	},
	toggleHasScheduleSyncFailed: ( state, hasFailed ) => {
		state.hasScheduleSyncFailed = hasFailed;
	},
	toggleIsAssigningTeam: ( state, isAssigning ) => {
		state.isAssigningTeam = isAssigning;
	},
	toggleIsFetchingMeetingSeries: ( state, isFetching ) => {
		state.isFetchingMeetingSeries = isFetching;
	},
	clearMeetingSeries: ( state, { meetingSeriesId, } ) => {
		const seriesMeetingIds = Object.keys( state.meetingSeriesMap[ meetingSeriesId ] );
		const scheduleMeetingIds = Object.keys( state.meetings );
		const pastMeetingIDs = difference( seriesMeetingIds, scheduleMeetingIds );

		state.meetingSeriesMap = {
			...omit( state.meetingSeriesMap, meetingSeriesId ),
			[ meetingSeriesId ]: {},
		};
		state.meetingSeriesOffsetMap[ meetingSeriesId ] = 0;

		const outcomeIdsToRemove = pastMeetingIDs.reduce( ( allOutcomeIds, pastMeetingId ) => {
			const meetingOutcomeIds = state.meetingOutcomeIdMap[ pastMeetingId ];
			return allOutcomeIds.concat( meetingOutcomeIds );
		}, [] );

		state.outcomeMap = omit( state.outcomeMap, outcomeIdsToRemove );
		state.meetingOutcomeIdMap = omit( state.meetingOutcomeIdMap, pastMeetingIDs );
	},
	toggleIsFetchingTeamGoals: ( state, isFetching ) => {
		state.isFetchingTeamGoals = isFetching;
	},
	/** TODO: commitments are a meeting agenda store concern - move */
	toggleIsChangingCommitmentCompletion: ( state, isChanging ) => {
		state.isChangingCommitmentCompletion = isChanging;
	},
	toggleIsConnectingTeam: ( state, isConnectingTeam ) => {
		state.isConnectingTeam = isConnectingTeam;
	},
	openCalendarOverviewVideoModal: state => {
		state.isOverviewVideoModalOpen = true;
	},
	closeCalendarOverviewVideoModal: state => {
		state.isOverviewVideoModalOpen = false;
	},
	toggleCalendarOverviewVideoModal: ( state, isOpen ) => {
		state.isOverviewVideoModalOpen = isOpen;
	},
};

export default {
	...reactions,
	...mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/schedule/mutations.js